import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment } from '@material-ui/core';
import { Cancel, Done } from '@material-ui/icons';
import { saveChecklist } from 'api';
import { AxiosError } from 'axios';
import { path as PageChecklistRegisterPath } from 'pages/PageChecklistRegister';
import AsyncSearch from 'components/AsyncSearch';
import useError from 'components/ErrorProvider/useError';
import TextField from 'components/TextField';
import { cleanNumberMask, clearPhoneNumberFormat, numberMask } from 'helper';
import { getRealEstates } from './api';
import { IRealEstate } from './interfaces';

export interface IProps {
  open: boolean;
  userPhoneNumber?: string;
  title?: string | null;
  onClose(): void;
  onSave(): void;
}

const NewChecklistDialog: React.FC<IProps> = ({ open, title, onClose, onSave, userPhoneNumber }) => {
  const history = useHistory();

  const [realEstate, setRealEstate] = useState<IRealEstate | null>(null);
  const [phoneNumber, setPhoneNumber] = useState(
    userPhoneNumber ? numberMask(clearPhoneNumberFormat(userPhoneNumber, '+7'), '(###) ### ## ##') : '',
  );
  const [disabled, setDisabled] = useState<boolean>(false);

  const { addError } = useError();

  const handleSave = async () => {
    if (!realEstate) {
      addError('Необходимо выбрать объект недвижимости');
      return;
    }
    if (!phoneNumber || cleanNumberMask(phoneNumber).length !== 10) {
      addError('Необходимо ввести корректный номер телефона');
      return;
    }

    setDisabled(true);
    saveChecklist(realEstate.guid, numberMask(clearPhoneNumberFormat(phoneNumber, '+7'), '+7 (###) ### ## ##'))
      .then(() => {
        onSave();
        history.push(PageChecklistRegisterPath);
      })
      .catch((error) => {
        addError((error as AxiosError).response?.data?.message);
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle>
        <b>{title ? title : 'Добавить'}</b>
      </DialogTitle>

      <DialogContent style={{ overflow: 'initial' }} dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AsyncSearch
              fetch={() => getRealEstates()}
              uniqueBy="guid"
              matchKeys={['name', 'originalRealEstateName', 'addressRu', 'addressKk']}
              value={realEstate}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.guid === value.guid}
              limitTags={1}
              disabled={disabled}
              onChange={(event, option) => setRealEstate(option)}
              renderInput={(props) => <TextField {...props} label="Объект недвижимости" variant="filled" />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Номер телефона"
              value={phoneNumber}
              mask="(###) ### ## ##"
              InputProps={{
                startAdornment: <InputAdornment position="start">+7</InputAdornment>,
              }}
              disabled={disabled}
              fullWidth
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" disabled={disabled} onClick={onClose} startIcon={<Cancel />}>
          Отменить
        </Button>

        <Button color="primary" disabled={disabled} variant="contained" onClick={handleSave} startIcon={<Done />}>
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewChecklistDialog;
